/*
 * <locations-filter> — client-side type-to-filter for the locations index.
 *
 * Every store is server-rendered so the list stays crawlable and works without JS; this
 * only hides cards that don't match. Matching runs against the data-location-* attributes
 * written by snippets/location-card.liquid rather than DOM text, so markup changes to the
 * card don't silently break the filter.
 */

const DEBOUNCE_MS = 120;

class LocationsFilter extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-locations-filter-input]');
    this.status = this.querySelector('[data-locations-filter-status]');

    if (!this.input) return;

    // The list lives outside this element, so scope the lookup to the whole app.
    this.section = this.closest('.locations-app') ?? document;
    this.noResults = this.section.querySelector('[data-locations-no-results]');
    this.regions = Array.from(this.section.querySelectorAll('[data-locations-region]'));

    this.cards = Array.from(this.section.querySelectorAll('[data-locations-filter-item]')).map((element) => ({
      element,
      haystack: [
        element.dataset.locationName,
        element.dataset.locationCity,
        element.dataset.locationAddress,
        element.dataset.locationZip,
      ]
        .filter(Boolean)
        .join(' '),
    }));

    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.apply(), DEBOUNCE_MS);
    });
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  apply() {
    const query = this.input.value.trim().toLowerCase();
    let visible = 0;

    for (const card of this.cards) {
      const matches = query === '' || card.haystack.includes(query);
      card.element.hidden = !matches;
      if (matches) visible += 1;
    }

    // A region group with nothing left to show takes its header with it.
    for (const region of this.regions) {
      region.hidden = !region.querySelector('[data-locations-filter-item]:not([hidden])');
    }

    if (this.noResults) this.noResults.hidden = visible !== 0;

    if (this.status) {
      this.status.textContent = `${visible} ${visible === 1 ? 'location' : 'locations'}`;
    }
  }
}

customElements.define('locations-filter', LocationsFilter);
